import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, ShieldCheck, Gauge, Layers, Award } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { BUSINESS, STATS } from "../lib/content";

export default function About() {
  const principles = [
    {
      icon: Layers,
      title: "Start Below The Surface",
      body:
        "Most paving failures trace back to poor base prep. We assess soil, drainage and grading before laying a single inch of asphalt.",
    },
    {
      icon: Gauge,
      title: "Engineered Thickness",
      body:
        "Asphalt depth is designed for how your surface will actually be used — not a one-size-fits-all approach.",
    },
    {
      icon: ShieldCheck,
      title: "Drainage First",
      body:
        "Water is the number one cause of pavement failure. We solve drainage before paving — always.",
    },
    {
      icon: Award,
      title: "Family Standards",
      body:
        "Three generations of craftsmanship. We treat every project like it's our own driveway.",
    },
  ];

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Our Company"
        title="Built on trust."
        accent="Paved with precision."
        subtitle={`Family owned since ${BUSINESS.established}. Over ${BUSINESS.yearsExperience} years of delivering durable paving solutions across Western Canada.`}
        testId="about-hero"
      />

      {/* Story */}
      <section className="py-24 md:py-32" data-testid="about-story">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] relative overflow-hidden border border-[#27272a]">
              <img
                src="/images/IMG_7.jpg"
                alt="North West Asphalt team on job site"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="aspect-square relative overflow-hidden border border-[#27272a]">
                <img
                  src="/images/IMG_3.jpg"
                  alt="Asphalt paving in progress"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square relative overflow-hidden border border-[#27272a]">
                <img
                  src="/images/IMG_11.jpg"
                  alt="Finished asphalt driveway"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="label-sm text-[#f59e0b] mb-5">Our story</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] mb-10">
              Four decades of paving.
              <br />
              <span className="text-[#f59e0b]">One family standard.</span>
            </h2>

            <div className="space-y-6 text-[#d4d4d8] text-lg leading-relaxed max-w-3xl">
              <p>
                North West Asphalt was founded in {BUSINESS.established} as a
                family owned paving company serving Western Canada. For over{" "}
                {BUSINESS.yearsExperience} years we've quietly built one of
                the most trusted reputations in the industry — one driveway,
                parking lot and roadway at a time.
              </p>
              <p>
                We've stayed family owned and operated because the best work is
                personal. When you call North West Asphalt, you're not dealing
                with a call center — you're dealing with the same people who
                will show up, prep the site properly, and stand behind the
                finished surface for years to come.
              </p>
              <p className="text-[#a1a1aa]">
                From residential driveways and commercial lots to large
                industrial and municipal projects, we bring the same disciplined
                approach: proper base prep, honest pricing, and hot-mix asphalt
                laid at the right thickness for real-world use.
              </p>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="inline-flex items-center gap-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
                data-testid="about-services-cta"
              >
                See our services
                <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
              </Link>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
                data-testid="about-call-cta"
              >
                <Phone className="w-4 h-4" strokeWidth={3} />
                Call the team
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section
        className="py-20 md:py-24 bg-[#050505] border-y border-[#27272a]"
        data-testid="about-stats"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x md:divide-[#27272a]">
          {STATS.map((s, i) => (
            <div key={i} className="md:px-10 first:md:pl-0 last:md:pr-0">
              <div className="font-display font-black text-6xl md:text-7xl text-[#f59e0b] leading-none mb-4">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 md:py-32" data-testid="about-principles">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <div className="label-sm text-[#f59e0b] mb-5">Our principles</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9]">
                The way we work.
              </h2>
            </div>
            <p className="lg:col-span-7 text-[#a1a1aa] text-lg leading-relaxed self-end">
              Our process is repeatable and disciplined. It's the reason driveways we paved in the 90s still look and perform as intended.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {principles.map((p, i) => (
              <div
                key={i}
                className="bg-[#18181b] border border-[#27272a] hover:border-[#f59e0b]/60 transition-colors p-8 md:p-12 group"
                data-testid={`principle-${i}`}
              >
                <div className="flex items-start gap-5 mb-5">
                  <div className="w-14 h-14 border border-[#f59e0b] text-[#f59e0b] flex items-center justify-center shrink-0 group-hover:bg-[#f59e0b] group-hover:text-[#09090b] transition-colors">
                    <p.icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <div className="font-display font-black text-3xl md:text-4xl uppercase leading-tight">
                    {p.title}
                  </div>
                </div>
                <p className="text-[#a1a1aa] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
